// `/acesso/:token` (5.4): troca o token do link por uma sessão de dispositivo e redireciona.
import { useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import type { DeviceSession } from "@centro-cirurgico/contracts";
import { Notice } from "@centro-cirurgico/ui";

import { DeviceMessagePage } from "../components/RequireDeviceSession";
import { LoadingState } from "../components/LoadingState";
import { errorMessage, exchangeAccessToken, isHttpError } from "../utils/api";
import { storeDeviceSession, storeLinkToken } from "../utils/session";

export function AccessPage() {
  const { token } = useParams<{ token: string }>();
  const [session, setSession] = useState<DeviceSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef<string | null>(null);
  const pendingRef = useRef<Promise<DeviceSession> | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Link incompleto. Peça um novo link à coordenação.");
      return;
    }
    // Só uma troca por token: o StrictMode monta o efeito duas vezes em desenvolvimento,
    // então a 2ª montagem não pode descartar a resposta da 1ª (a promessa fica na ref).
    if (startedRef.current !== token) {
      startedRef.current = token;
      pendingRef.current = exchangeAccessToken(token);
    }
    let active = true;
    pendingRef.current
      ?.then((next) => {
        if (!active) return;
        storeLinkToken(token);
        storeDeviceSession(next);
        setSession(next);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        const invalid = isHttpError(cause) && (cause.status === 401 || cause.status === 404 || cause.status === 410);
        setError(
          invalid
            ? "Link revogado ou expirado. Peça um novo link à coordenação."
            : errorMessage(cause, "Não foi possível validar o link de acesso.")
        );
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (session) return <Navigate to={session.mode === "reception" ? "/recepcao" : "/sala"} replace />;

  if (error) {
    return (
      <DeviceMessagePage icon={<KeyRound aria-hidden="true" size={40} />} title="Link de acesso inválido">
        <Notice error={error} />
        <p className="subtle">Os links são de uso único por dispositivo e podem ser revogados pela coordenação.</p>
      </DeviceMessagePage>
    );
  }

  return (
    <div className="app">
      <LoadingState message="Validando link de acesso…" />
    </div>
  );
}
